<div class="new-customer-page">
  <div class="page-header">
    <button mat-icon-button type="button" (click)="back()" aria-label="Back">
      <mat-icon>arrow_back</mat-icon>
    </button>
    <div>
      <h2>New Customer</h2>
      <div class="route-caption" *ngIf="selectedRoute">{{ selectedRoute.route }}</div>
    </div>
  </div>

  <div class="form-card">
    <label>Customer / Shop Name *</label>
    <input [ngModel]="name" (ngModelChange)="onCustomerNameChange($event)" autocomplete="organization" placeholder="Shop name" style="text-transform: uppercase" [class.field-invalid]="nameInvalid" [attr.aria-invalid]="nameInvalid" />

    <div class="two-col">
      <div>
        <label>Phone *</label>
        <input [(ngModel)]="phone" inputmode="tel" autocomplete="tel" placeholder="Phone number" [class.field-invalid]="phoneInvalid" [attr.aria-invalid]="phoneInvalid" />
      </div>
      <div>
        <label>WhatsApp Number</label>
        <input [(ngModel)]="whatsappNumber" inputmode="tel" autocomplete="tel" placeholder="WhatsApp number" />
      </div>
    </div>

    <div class="two-col">
      <div>
        <label>GST Treatment *</label>
        <select [(ngModel)]="gstTreatment" [disabled]="loadingOptions" [class.field-invalid]="gstTreatmentInvalid" [attr.aria-invalid]="gstTreatmentInvalid">
          <option *ngFor="let treatment of gstTreatments" [ngValue]="treatment.value">{{ treatment.label }}</option>
        </select>
      </div>
      <div>
        <label>State</label>
        <select [(ngModel)]="selectedStateId" [disabled]="loadingOptions">
          <option [ngValue]="null">Select state</option>
          <option *ngFor="let state of states" [ngValue]="state.id">{{ state.name }}</option>
        </select>
        <div class="hint compact" *ngIf="countryName">{{ countryName }} · defaults from company</div>
      </div>
    </div>

    <label>GST / VAT No.<span *ngIf="gstNumberRequired"> *</span></label>
    <div class="gst-row">
      <input [(ngModel)]="vat" autocomplete="off" maxlength="15" placeholder="e.g. 32ABCDE1234F1Z5" style="text-transform: uppercase" [class.field-invalid]="vatInvalid" [attr.aria-invalid]="vatInvalid" />
      <button mat-stroked-button type="button" (click)="lookupGstin()" [disabled]="gstLookupBusy || !vat.trim()">
        <mat-icon>manage_search</mat-icon>
        {{ gstLookupBusy ? 'Fetching...' : 'Fetch Details' }}
      </button>
    </div>
    <div class="hint" *ngIf="gstLookupMessage">{{ gstLookupMessage }}</div>
    <div class="error-text" *ngIf="gstLookupError">{{ gstLookupError }}</div>
    <div class="hint">Fetch Details uses the GST/company autocomplete already available in Odoo when that service is configured.</div>

    <label>Customer Tags *</label>
    <div class="tag-picker">
      <button class="tag-picker-trigger" type="button" (click)="tagsOpen = !tagsOpen" [disabled]="loadingOptions" [class.field-invalid]="tagsInvalid" [attr.aria-invalid]="tagsInvalid">
        <span>{{ selectedTagSummary }}</span>
        <mat-icon>{{ tagsOpen ? 'expand_less' : 'expand_more' }}</mat-icon>
      </button>
      <div class="tag-menu" *ngIf="tagsOpen">
        <div class="tag-empty" *ngIf="!tags.length">No customer tags are available in Odoo.</div>
        <label class="tag-option" *ngFor="let tag of tags">
          <input type="checkbox" [checked]="isTagSelected(tag.id)" (change)="toggleTag(tag.id, $any($event.target).checked)" />
          <span>{{ tag.name }}</span>
        </label>
        <button mat-button type="button" class="tag-done" (click)="tagsOpen = false">Done</button>
      </div>
    </div>

    <label>Address</label>
    <input [(ngModel)]="street" autocomplete="street-address" placeholder="Street / Building" />
    <input [(ngModel)]="street2" autocomplete="address-line2" placeholder="Area / Landmark" />

    <div class="two-col">
      <div>
        <label>City / Area</label>
        <input [(ngModel)]="city" autocomplete="address-level2" />
      </div>
      <div>
        <label>PIN / ZIP</label>
        <input [(ngModel)]="zip" autocomplete="postal-code" />
      </div>
    </div>

    <div class="two-col">
      <div>
        <label>Route Segment *</label>
        <select [(ngModel)]="selectedSegmentId" [class.field-invalid]="segmentInvalid" [attr.aria-invalid]="segmentInvalid">
          <option *ngFor="let segment of segments" [ngValue]="segment.id">{{ segment.name }}</option>
        </select>
      </div>
      <div>
        <label>Pricelist *</label>
        <select [(ngModel)]="selectedPriceListId" [class.field-invalid]="pricelistInvalid" [attr.aria-invalid]="pricelistInvalid">
          <option [ngValue]="null">Select pricelist</option>
          <option *ngFor="let pricelist of priceLists" [ngValue]="pricelist.id">{{ pricelist.pricelist_name }}</option>
        </select>
      </div>
    </div>
  </div>

  <div class="form-card">
    <h3>Shop Location</h3>
    <div class="button-row">
      <button mat-raised-button color="primary" type="button" (click)="captureCurrentLocation()" [disabled]="locating">
        <mat-icon>my_location</mat-icon>
        {{ locating ? 'Getting Location...' : 'Capture Current Location' }}
      </button>
      <button mat-button type="button" *ngIf="latitude != null && longitude != null" (click)="openCapturedLocation()">
        <mat-icon>map</mat-icon> View Map
      </button>
    </div>
    <div class="location-info" *ngIf="latitude != null && longitude != null">
      {{ latitude | number:'1.5-6' }}, {{ longitude | number:'1.5-6' }}
      <span *ngIf="gpsAccuracyM"> · Accuracy ±{{ gpsAccuracyM | number:'1.0-0' }} m</span>
      <div class="warning" *ngIf="(gpsAccuracyM || 0) > 200">GPS accuracy is low; confirm the shop location before saving.</div>
    </div>
    <div class="error-text" *ngIf="locationError">{{ locationError }}</div>
  </div>

  <div class="form-card">
    <h3>Shop Photo</h3>
    <input #photoInput class="file-input" type="file" accept="image/*" capture="environment" (change)="onPhotoSelected($event)" />
    <div class="button-row">
      <button mat-raised-button type="button" (click)="photoInput.click()">
        <mat-icon>photo_camera</mat-icon> Take / Choose Photo
      </button>
      <button mat-button type="button" *ngIf="imagePreview" (click)="clearPhoto()">Remove</button>
    </div>
    <img *ngIf="imagePreview" class="photo-preview" [src]="imagePreview" alt="Shop photo preview" />
    <div class="hint">Photo is optional and compressed before upload to reduce mobile data usage.</div>
    <div class="error-text" *ngIf="photoError">{{ photoError }}</div>
  </div>

  <div class="match-card" *ngIf="duplicateMatches.length">
    <h3>Possible Existing Customers</h3>
    <p>These are suggestions only. You can use an existing customer or create a new one anyway.</p>

    <div class="match-row" *ngFor="let match of duplicateMatches">
      <div class="match-photo">
        <img *ngIf="match.customer.image" [src]="'data:' + (match.customer.imageMimeType || 'image/jpeg') + ';base64,' + match.customer.image" alt="Shop" />
        <mat-icon *ngIf="!match.customer.image">store</mat-icon>
      </div>
      <div class="match-info">
        <strong>{{ match.customer.name }}</strong>
        <span *ngIf="match.customer.phone">{{ match.customer.phone }}</span>
        <span *ngIf="distanceLabel(match)">{{ distanceLabel(match) }}</span>
        <small>{{ match.reasons.join(' · ') }}</small>
      </div>
      <div class="match-actions">
        <button mat-button type="button" *ngIf="match.customer.latitude && match.customer.longitude" (click)="openMatchMap(match)">
          <mat-icon>map</mat-icon> Map
        </button>
        <button mat-raised-button color="primary" type="button" (click)="useExisting(match)" [disabled]="saving">Use Existing</button>
      </div>
    </div>

    <div class="error-box match-action-error" *ngIf="formError">{{ formError }}</div>

    <button mat-raised-button color="accent" type="button" class="create-anyway" (click)="saveCustomer(true)" [disabled]="saving">
      Create New Anyway
    </button>
  </div>

  <div class="action-error-area" *ngIf="!duplicateMatches.length && ((validationAttempted && validationMessages.length) || formError)">
    <div class="validation-summary" *ngIf="validationAttempted && validationMessages.length">
      <strong>Please correct the highlighted fields:</strong>
      <div class="validation-badges">
        <span class="validation-badge" *ngFor="let message of validationMessages">{{ message }}</span>
      </div>
    </div>
    <div class="error-box" *ngIf="formError">{{ formError }}</div>
  </div>

  <div class="footer-actions" *ngIf="!duplicateMatches.length">
    <button mat-button type="button" (click)="back()" [disabled]="saving">Cancel</button>
    <button mat-raised-button color="primary" type="button" (click)="saveCustomer(false)" [disabled]="saving || checkingDuplicates || loadingOptions">
      {{ checkingDuplicates ? 'Checking...' : (saving ? 'Saving...' : 'Save & Start Sale') }}
    </button>
  </div>
</div>
